<template>
  <div class="invoice-create-page main-content">
    <form v-if="!initLoading" action="" @submit.prevent="submitInvoiceData">
      <div class="page-header">
        <h3 class="page-title">{{ isEdit ? $t('islamic_finance.edit_offer_to_Sell') : $t('islamic_finance.new_offer_to_Sell') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/supplier/dashboard">{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              slot="item-title"
              to="#">
              {{ $t('islamic_finance.title') }}
            </router-link>
          </li>
          <li class="breadcrumb-item">{{ isEdit ? $t('islamic_finance.edit_offer_to_Sell') : $t('islamic_finance.new_offer_to_Sell') }}</li>
        </ol>
        <div class="page-actions row">
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            icon="save"
            color="theme"
            class="invoice-action-btn"
            type="submit">

            {{ isEdit ? $t('islamic_finance.update_offer_to_Sell') : $t('islamic_finance.save_offer_to_Sell') }}
          </base-button>
        </div>
      </div>
      <div class="row invoice-input-group">
        <div class="col-md-5 invoice-customer-container">
          <div v-if="selectedCustomer" class="show-customer">
            <div class="row px-2 mt-1">
              <div v-if="selectedCustomer.billing_address" class="col col-6">
                <div class="row address-menu">
                  <label class="col-sm-4 px-2 title">{{ $t('general.bill_to') }}</label>
                  <div class="col-sm p-0 px-2 content">
                    <label v-if="selectedCustomer.billing_address.name">
                      {{ selectedCustomer.billing_address.name }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.address_street_1">
                      {{ selectedCustomer.billing_address.address_street_1 }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.address_street_2">
                      {{ selectedCustomer.billing_address.address_street_2 }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.city && selectedCustomer.billing_address.state">
                      {{ selectedCustomer.billing_address.city }}, {{ selectedCustomer.billing_address.state }} {{ selectedCustomer.billing_address.zip }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.country">
                      {{ selectedCustomer.billing_address.country.name }}
                    </label>
                    <label v-if="selectedCustomer.billing_address.phone">
                      {{ selectedCustomer.billing_address.phone }}
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="selectedCustomer.shipping_address" class="col col-6">
                <div class="row address-menu">
                  <label class="col-sm-4 px-2 title">{{ $t('general.ship_to') }}</label>
                  <div class="col-sm p-0 px-2 content">
                    <label v-if="selectedCustomer.shipping_address.name">
                      {{ selectedCustomer.shipping_address.name }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.address_street_1">
                      {{ selectedCustomer.shipping_address.address_street_1 }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.address_street_2">
                      {{ selectedCustomer.shipping_address.address_street_2 }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.city && selectedCustomer.shipping_address">
                      {{ selectedCustomer.shipping_address.city }}, {{ selectedCustomer.shipping_address.state }} {{ selectedCustomer.shipping_address.zip }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.country" class="country">
                      {{ selectedCustomer.shipping_address.country.name }}
                    </label>
                    <label v-if="selectedCustomer.shipping_address.phone" class="phone">
                      {{ selectedCustomer.shipping_address.phone }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="customer-content mb-1">
              <label class="email">{{ selectedCustomer.name }}</label>
              <label class="action" @click="editCustomer">{{ $t('general.edit') }}</label>
              <label class="action" @click="removeCustomer">{{ $t('general.deselect') }}</label>
            </div>
          </div>

          <base-popup v-else :class="['add-customer', {'customer-required': $v.selectedCustomer.$error}]" >
            <div slot="activator" class="add-customer-action">
              <font-awesome-icon icon="user" class="customer-icon"/>
              <div>
                <label>{{ $t('customers.new_customer') }} <span class="text-danger"> * </span></label>
                <p v-if="$v.selectedCustomer.$error && !$v.selectedCustomer.required" class="text-danger">
                  {{ $t('validation.required') }}
                </p>
              </div>
            </div>
            <customer-select-popup type="offerToSell" />
          </base-popup>
        </div>
        <div class="col invoice-input">
          <div class="row mb-3">
            <div class="col collapse-input">
              <label>{{ $t('islamic_finance.title') }} {{ $t('invoices.date') }}<span class="text-danger"> * </span></label>
              <base-date-picker
                v-model="newInvoice.offer_to_sell_date"
                :calendar-button="true"
                calendar-button-icon="calendar"
                @change="$v.newInvoice.offer_to_sell_date.$touch()"
              />
              <span v-if="$v.newInvoice.offer_to_sell_date.$error && !$v.newInvoice.offer_to_sell_date.required" class="text-danger"> {{ $t('validation.required') }} </span>
            </div>
            <div class="col collapse-input">
              <label>{{ $t('islamic_finance.delivery_date') }}<span class="text-danger"> * </span></label>
              <base-date-picker
                v-model="newInvoice.delivery_date"
                :invalid="$v.newInvoice.delivery_date.$error"
                :calendar-button="true"
                :disabled="true"
                calendar-button-icon="calendar"
                @change="$v.newInvoice.delivery_date.$touch()"
              />
              <span v-if="$v.newInvoice.delivery_date.$error && !$v.newInvoice.delivery_date.required" class="text-danger mt-1"> {{ $t('validation.required') }}</span>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col collapse-input">
              <label>{{ $t('islamic_finance.payment_date') }}<span class="text-danger"> * </span></label>
              <base-date-picker
                v-model="newInvoice.payment_date"
                :invalid="$v.newInvoice.payment_date.$error"
                :calendar-button="true"
                :disabled="true"
                calendar-button-icon="calendar"
                @change="$v.newInvoice.payment_date.$touch()"
              />
              <span v-if="$v.newInvoice.payment_date.$error && !$v.newInvoice.payment_date.required" class="text-danger mt-1"> {{ $t('validation.required') }}</span>
            </div>
            <!-- <div class="col collapse-input">
              <label>{{ $t('invoices.po_ref_number') }}</label>
              <base-input
                v-model="newInvoice.po_reference_number"
                :invalid="$v.newInvoice.po_reference_number.$error"
                icon="hashtag"
                @input="$v.newInvoice.po_reference_number.$touch()"
              />
              <div
                v-if="$v.newInvoice.po_reference_number.$error"
                class="text-danger"
              >
                {{ $tc('validation.po_ref_number_maxlength') }}
              </div>
            </div> -->
          </div>

        </div>
      </div>
      <table class="item-table">
        <colgroup>
          <col style="width: 2%;">
          <col style="width: 20%;">
          <col style="width: 20%;">
          <col style="width: 10%;">
          <col style="width: 15%;">
          <col v-if="discountPerItem === 'YES'" style="width: 15%;">
          <col style="width: 15%;">
        </colgroup>
        <thead class="item-table-header">
          <tr>
            <th class="text-left">
              <span class="column-heading item-checkbox"/>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $tc('items.item',2) }}
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $tc('items.part_number',2) }}
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $t('invoices.item.quantity') }}
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $t('invoices.item.price') }}
              </span>
            </th>
            <th v-if="discountPerItem === 'YES'" class="text-right">
              <span class="column-heading">
                {{ $t('invoices.item.discount') }}
              </span>
            </th>
            <th class="text-right">
              <span class="column-heading amount-heading">
                {{ $t('invoices.item.amount') }}
              </span>
            </th>
          </tr>
        </thead>
        <draggable v-model="newInvoice.items" class="item-body" tag="tbody" handle=".handle">
          <offer-to-sell-item
            v-for="(item, index) in newInvoice.items"
            :key="item.id"
            :index="index"
            :item-data="item"
            :invoice-items="newInvoice.items"
            :currency="currency"
            :tax-per-item="taxPerItem"
            :discount-per-item="discountPerItem"
            :type="OfferToSellItemType"
            @remove="removeItem"
            @update="updateItem"
            @itemValidate="checkItemsData"
          />
        </draggable>
      </table>
      <div class="add-item-action" @click="addItem">
        <font-awesome-icon icon="shopping-basket" class="mr-2"/>
        {{ $t('invoices.add_item') }}
      </div>

      <div class="invoice-foot">
        <div>
          <label>{{ $t('invoices.notes') }}</label>
          <base-text-area
            v-model="newInvoice.notes"
            rows="3"
            cols="50"

          />

        </div>

        <div class="invoice-total">
          <div class="section">
            <label class="invoice-label">{{ $t('invoices.sub_total') }}</label>
            <label class="invoice-amount">
              <div v-html="$utils.formatMoney(subtotal, currency)" />
            </label>
          </div>
          <div v-if="taxPerItem === 'NO' || taxPerItem === null">
            <tax
              v-for="(tax, index) in newInvoice.taxes"
              :index="index"
              :total="subtotalWithDiscount"
              :key="tax.id"
              :tax="tax"
              :taxes="newInvoice.taxes"
              :currency="currency"
              :total-tax="totalSimpleTax"
              @remove="removeInvoiceTax"
              @update="updateTax"
            />
          </div>

          <div class="section border-top mt-3">
            <label class="invoice-label">{{ $t('invoices.total') }} {{ $t('invoices.amount') }}:</label>
            <label class="invoice-amount total">
              <div v-html="$utils.formatMoney(total, currency)" />
            </label>
          </div>
        </div>
      </div>
    </form>
    <base-loader v-else />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import MultiSelect from 'vue-multiselect'
import OfferToSellItem from './Item'
import offerToSellStub from '../../../stub/offerToSell'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { validationMixin } from 'vuelidate'
import Guid from 'guid'
import TaxStub from '../../../stub/tax'
import Tax from './OfferToSellTax'
const { required, between, maxLength } = require('vuelidate/lib/validators')

export default {
  components: {
    OfferToSellItem,
    MultiSelect,
    Tax,
    draggable
  },
  mixins: [validationMixin],
  data () {
    return {
      newInvoice: {
        po_reference_number: null,
        offer_to_sell_date: null,
        delivery_date: null,
        invoice_number: null,
        customer_id: null,
        invoice_template_id: 1,
        sub_total: null,
        total: null,
        tax: null,
        notes: null,
        discount_type: 'fixed',
        discount_val: 0,
        discount: 0,
        payment_date: null,
        po_payment_date: null,
        items: [{
          ...offerToSellStub,
          id: Guid.raw(),
          taxes: [{...TaxStub, id: Guid.raw()}]
        }],
        taxes: []
      },
      customers: [],
      itemList: [],
      invoiceTemplates: [],
      selectedCurrency: '',
      taxPerItem: null,
      approval_days: null,
      discountPerItem: null,
      initLoading: false,
      isLoading: false,
      maxDiscount: 0,
      invoicePrefix: null,
      invoiceNumAttribute: null,
      documentTypes: [],
      defaultInput: {
        decimal: '.',
        thousands: ',',
        prefix: '% ',
        precision: 2,
        masked: false
      },
      purchase_order_id: null,
      OfferToSellItemType: 'Invoice',
      userType: ''
    }
  },
  validations () {
    return {
      newInvoice: {
        po_reference_number: {
          maxLength: maxLength(255)
        },
        offer_to_sell_date: {
          required
        },
        delivery_date: {
          required
        },
        discount_val: {
          between: between(0, this.subtotal)
        },

        payment_date: {
          required
        }
      },
      selectedCustomer: {
        required
      }
    }
  },
  computed: {

    ...mapGetters('documentType', [
      'attachments'
    ]),
    ...mapGetters('general', [
      'itemDiscount'
    ]),
    ...mapGetters('currency', [
      'defaultCurrency'
    ]),
    ...mapGetters('OfferToSell', [
      'getTemplateId',
      'selectedCustomer'
    ]),
    isEdit () {
      if (this.$route.name === 'offer-to-sell.edit') {
        return true
      }
      return false
    },
    currency () {
      return this.selectedCurrency
    },
    subtotalWithDiscount () {
      const sum = this.newInvoice.items.reduce((a, b) => {
        return a + (Number(b.total) || 0)
      }, 0)
      const precision = this.currency && this.currency.precision != null ? this.currency.precision : 2
      const discounted = sum - this.newInvoice.discount_val
      return this.$utils.roundToPrecision(discounted, precision)
    },
    total () {
      const sum = this.newInvoice.items.reduce((a, b) => {
        return a + (Number(b.total) || 0)
      }, 0)
      const precision = this.currency && this.currency.precision != null ? this.currency.precision : 2
      return this.$utils.roundToPrecision(sum - this.newInvoice.discount_val + this.totalTax, precision)
    },
    subtotal () {
      const sum = this.newInvoice.items.reduce((a, b) => {
        return a + this.$utils.roundToPrecision(Number(b.total) || 0, (this.currency && this.currency.precision != null ? this.currency.precision : 2))
      }, 0)
      const precision = this.currency && this.currency.precision != null ? this.currency.precision : 2
      return this.$utils.roundToPrecision(sum, precision)
    },
    discount: {
      get: function () {
        return this.newInvoice.discount
      },
      set: function (newValue) {
        if (this.newInvoice.discount_type === 'percentage') {
          this.newInvoice.discount_val = (this.subtotal * newValue) / 100
        } else {
          this.newInvoice.discount_val = newValue * 100
        }

        this.newInvoice.discount = newValue
      }
    },
    totalSimpleTax () {
      return window._.sumBy(this.newInvoice.taxes, function (tax) {
        if (!tax.compound_tax) {
          return tax.amount
        }

        return 0
      })
    },

    totalCompoundTax () {
      return window._.sumBy(this.newInvoice.taxes, function (tax) {
        if (tax.compound_tax) {
          return tax.amount
        }

        return 0
      })
    },
    totalTax () {
      if (this.taxPerItem === 'NO' || this.taxPerItem === null) {
        return this.totalSimpleTax + this.totalCompoundTax
      }

      return window._.sumBy(this.newInvoice.items, function (tax) {
        return tax.tax
      })
    },
    allTaxes () {
      let taxes = []

      this.newInvoice.items.forEach((item) => {
        item.taxes.forEach((tax) => {
          let found = taxes.find((_tax) => {
            return _tax.tax_type_id === tax.tax_type_id
          })

          if (found) {
            found.amount += tax.amount
          } else if (tax.tax_type_id) {
            taxes.push({
              tax_type_id: tax.tax_type_id,
              amount: tax.amount,
              percent: tax.percent,
              name: tax.name
            })
          }
        })
      })

      return taxes
    },
    itemTotal () {
      const price = Number(this.itemData.price) || 0
      const discount = Number(this.itemData.discount) || 0
      const quantity = Number(this.itemData.quantity) || 0
      return (price - discount) * quantity
    }
  },
  watch: {
    async selectedCustomer (newVal) {
      if (newVal && newVal.currency) {
        this.selectedCurrency = newVal.currency
      } else {
        this.selectedCurrency = this.defaultCurrency
      }

      if (newVal && newVal.id) {
        const res = await this.fetchPaymentApprovalDays(newVal.id)
        if (res.data.success === true && res.data.approval_days) {
          this.approval_days = parseInt(res.data.approval_days)
        }
      }
    },
    approval_days (newVal) {
      if (newVal && this.newInvoice.offer_to_sell_date) {
        this.newInvoice.payment_date = this.computePaymentDate(this.newInvoice.offer_to_sell_date, newVal)
      }
    },
    'newInvoice.offer_to_sell_date' (newVal) {
      if (newVal && this.approval_days) {
        this.newInvoice.payment_date = this.computePaymentDate(newVal, this.approval_days)
      }
      // Sync delivery_date with offer_to_sell_date
      if (newVal) {
        this.newInvoice.delivery_date = newVal
      }
    },
    subtotal (newValue) {
      if (this.newInvoice.discount_type === 'percentage') {
        this.newInvoice.discount_val = (this.newInvoice.discount * newValue) / 100
      }
    }
  },
  created () {
    if (localStorage.getItem('role') === 'super_admin') {
      this.userType = 'super_admin'
    } else if (localStorage.getItem('account_type') === 'anchor') {
      this.userType = 'anchor'
    } else if (localStorage.getItem('account_type') === 'funder') {
      this.userType = 'funder'
    } else if (localStorage.getItem('account_type') === 'supplier') {
      this.userType = 'supplier'
    }
    this.loadData()
    this.fetchInitialItems()
    this.resetSelectedCustomer()
    window.hub.$on('newTax', this.onSelectTax)
  },
  methods: {
    ...mapActions('modal', [
      'openModal'
    ]),
    ...mapActions('OfferToSell', [
      'addOfferToSell',
      'fetchCreateInvoice',
      'fetchOfferToSell',
      'resetSelectedCustomer',
      'selectCustomer',
      'updateOfferToSell'
    ]),
    ...mapActions('item', [
      'fetchItems'
    ]),
    ...mapActions('documentType', [
      'deleteAttachment'
    ]),
    ...mapActions('po', [
      'fetchPOItems'
    ]),
    ...mapActions('invoice', [
      'fetchPaymentApprovalDays'
    ]),
    computePaymentDate (offerDate, approvalDays) {
      if (!offerDate || approvalDays == null) {
        return null
      }

      const base = moment(offerDate).add(approvalDays + 1, 'days')
      const day = base.day()

      if (day === 6) { // Saturday -> move to Monday
        base.add(2, 'days')
      } else if (day === 0) { // Sunday -> move to Monday
        base.add(1, 'days')
      }

      return base.toString()
    },
    selectFixed () {
      if (this.newInvoice.discount_type === 'fixed') {
        return
      }

      this.newInvoice.discount_val = this.newInvoice.discount * 100
      this.newInvoice.discount_type = 'fixed'
    },
    selectPercentage () {
      if (this.newInvoice.discount_type === 'percentage') {
        return
      }

      this.newInvoice.discount_val = (this.subtotal * this.newInvoice.discount) / 100

      this.newInvoice.discount_type = 'percentage'
    },
    updateTax (data) {
      Object.assign(this.newInvoice.taxes[data.index], {...data.item})
    },
    async fetchInitialItems () {
      await this.fetchItems({
        filter: {},
        orderByField: '',
        orderBy: '',
        pagination: 'no'
      })
    },
    async loadData () {
      if (this.isEdit) {
        this.initLoading = true
        let response = await this.fetchOfferToSell(this.$route.params.id)

        if (response.data) {
          this.selectCustomer(response.data.invoice.customer_id)
          this.newInvoice = response.data.invoice
          this.newInvoice.items = this.newInvoice.items.map(item => ({
            ...item,
            discount: item.discount !== undefined ? item.discount : 0,
            discount_type: item.discount_type || 'fixed',
            discount_val: item.discount_val !== undefined ? item.discount_val : 0,
            taxes: item.taxes || []
          }))

          this.discountPerItem = response.data.discount_per_item
          this.taxPerItem = response.data.tax_per_item
          this.selectedCurrency = this.defaultCurrency
          this.invoiceTemplates = response.data.invoiceTemplates
          this.invoicePrefix = response.data.invoice_prefix
        }

        this.initLoading = false

        return
      }

      this.initLoading = true
      let response = await this.fetchCreateInvoice()
      if (response.data) {
        this.discountPerItem = response.data.discount_per_item
        this.taxPerItem = response.data.tax_per_item
        this.selectedCurrency = this.defaultCurrency
        this.invoiceTemplates = response.data.invoiceTemplates
        let today = new Date()
        this.newInvoice.offer_to_sell_date = moment(today).toString()
        this.newInvoice.delivery_date = moment(today).toString()
        this.itemList = response.data.items
        this.invoicePrefix = response.data.invoice_prefix
      }
      this.initLoading = false
    },
    removeCustomer () {
      this.resetSelectedCustomer()
    },
    editCustomer () {
      this.openModal({
        'title': this.$t('customers.edit_customer'),
        'componentName': 'CustomerModal',
        'id': this.selectedCustomer.id,
        'data': this.selectedCustomer
      })
    },
    openTemplateModal () {
      this.openModal({
        'title': this.$t('general.choose_template'),
        'componentName': 'InvoiceTemplate',
        'data': this.invoiceTemplates
      })
    },
    addItem () {
      this.newInvoice.items.push({...offerToSellStub, id: Guid.raw(), taxes: [{...TaxStub, id: Guid.raw()}]})
    },
    removeItem (index) {
      this.newInvoice.items.splice(index, 1)
    },
    updateItem (data) {
      Object.assign(this.newInvoice.items[data.index], {...data.item})
    },
    async submitInvoiceData () {
      this.documentTypes.length = 0
      if (!this.checkValid()) {
        return false
      }

      this.isLoading = true

      let data = {
        ...this.newInvoice,
        offer_to_sell_date: moment(this.newInvoice.offer_to_sell_date).format('DD/MM/YYYY'),
        delivery_date: moment(this.newInvoice.delivery_date).format('DD/MM/YYYY'),
        payment_date: moment(this.newInvoice.payment_date).format('DD/MM/YYYY'),
        sub_total: this.subtotal,
        total: this.total,
        tax: this.totalTax,
        customer_id: null,
        invoice_template_id: this.getTemplateId
      }
      if (this.selectedCustomer != null) {
        data.customer_id = this.selectedCustomer.id
      }

      if (this.isEdit) {
        this.submitUpdate(data)
        return
      }
      data.funder_user_id = this.$route.params.id
      let res = await this.addOfferToSell(data)
      if (res.data.result === 'success') {
        window.toastr['success'](this.$t('islamic_finance.created_message'))
        this.$router.push(`/supplier/funders/${this.$route.params.id}/islamic_finance`)
        this.isLoading = false
      } else {
        this.isLoading = false
      }
    },
    submitUpdate (data) {
      this.updateOfferToSell(data).then((res) => {
        this.isLoading = false
        if (res.data.success) {
          window.toastr['success'](this.$t('islamic_finance.updated_message'))
          this.$router.push(`/supplier/funders/${res.data.islamicFinance.funder_user_id}/islamic_finance`)
        }
      }).catch((err) => {
        this.isLoading = false
        if (err.response.data.errors.invoice_number) {
          window.toastr['error'](err.response.data.errors.invoice_number)
          return true
        }
        console.log(err)
      })
    },
    checkItemsData (index, isValid) {
      this.newInvoice.items[index].valid = isValid
    },
    onSelectTax (selectedTax) {
      let amount = 0

      if (selectedTax.compound_tax && this.subtotalWithDiscount) {
        amount = ((this.subtotalWithDiscount + this.totalSimpleTax) * selectedTax.percent) / 100
      } else if (this.subtotalWithDiscount && selectedTax.percent) {
        amount = (this.subtotalWithDiscount * selectedTax.percent) / 100
      }

      this.newInvoice.taxes.push({
        ...TaxStub,
        id: Guid.raw(),
        name: selectedTax.name,
        percent: selectedTax.percent,
        compound_tax: selectedTax.compound_tax,
        tax_type_id: selectedTax.id,
        amount
      })

      this.$refs.taxModal.close()
    },
    removeInvoiceTax (index) {
      this.newInvoice.taxes.splice(index, 1)
    },
    checkValid () {
      this.$v.newInvoice.$touch()
      this.$v.selectedCustomer.$touch()

      window.hub.$emit('checkItems')
      let isValid = true
      this.newInvoice.items.forEach((item) => {
        if (!item.valid) {
          isValid = false
        }
      })
      if (!this.$v.selectedCustomer.$invalid && this.$v.newInvoice.$invalid === false && isValid === true) {
        return true
      }
      return false
    },
    onkeypress (event, input) {
      if (event.which === 45) {
        event.preventDefault()
      }
    }
  }
}
</script>
<style scoped>
.item-checkbox{
  padding-left: 20px;
}
</style>
